import { parseARS, PRICE_OP_TYPES, ROUNDING_MODES, type BulkPriceInput } from '@gl/shared';
import { z } from 'zod';

export const SCOPE_MODES = ['all', 'categories', 'items'] as const;
export type ScopeMode = (typeof SCOPE_MODES)[number];

/** "15", "12,5", "12.5" → 15 / 12.5. */
export function parsePercent(input: string): number | null {
  const s = input.replace(/[%\s]/g, '').replace(',', '.');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export const wizardSchema = z
  .object({
    scopeMode: z.enum(SCOPE_MODES),
    categoryIds: z.array(z.string()),
    itemIds: z.array(z.string()),
    includeVariants: z.boolean(),
    opType: z.enum(PRICE_OP_TYPES),
    direction: z.enum(['UP', 'DOWN']),
    amount: z.string(),
    roundingMode: z.enum(ROUNDING_MODES),
    stepCents: z.number().int().positive(),
    note: z.string().trim().max(200, 'Máximo 200 caracteres'),
  })
  .superRefine((v, ctx) => {
    if (v.scopeMode === 'categories' && v.categoryIds.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['categoryIds'], message: 'Elegí al menos una categoría' });
    }
    if (v.scopeMode === 'items' && v.itemIds.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['itemIds'], message: 'Elegí al menos un producto' });
    }
    if (v.opType === 'PERCENT') {
      const n = parsePercent(v.amount);
      if (n === null || n <= 0) ctx.addIssue({ code: 'custom', path: ['amount'], message: 'Ingresá un porcentaje mayor a 0' });
      else if (v.direction === 'DOWN' && n > 100) {
        ctx.addIssue({ code: 'custom', path: ['amount'], message: 'No se puede bajar más del 100 %' });
      } else if (n > 1000) ctx.addIssue({ code: 'custom', path: ['amount'], message: 'Máximo 1000 %' });
    } else {
      const cents = parseARS(v.amount);
      if (cents === null || cents <= 0) ctx.addIssue({ code: 'custom', path: ['amount'], message: 'Ingresá un monto mayor a $ 0' });
    }
  });

export type WizardValues = z.infer<typeof wizardSchema>;

export const WIZARD_DEFAULTS: WizardValues = {
  scopeMode: 'all',
  categoryIds: [],
  itemIds: [],
  includeVariants: true,
  opType: 'PERCENT',
  direction: 'UP',
  amount: '',
  roundingMode: 'NEAREST',
  stepCents: 10000,
  note: '',
};

/** Campos que valida cada paso (para `form.trigger`). */
export const STEP_FIELDS: Array<Array<keyof WizardValues>> = [
  ['scopeMode', 'categoryIds', 'itemIds', 'includeVariants'],
  ['opType', 'direction', 'amount'],
  ['roundingMode', 'stepCents', 'note'],
  [],
];

export function toBulkBody(v: WizardValues): BulkPriceInput {
  const sign = v.direction === 'DOWN' ? -1 : 1;
  let value: number;
  if (v.opType === 'PERCENT') value = sign * (parsePercent(v.amount) ?? 0);
  else if (v.opType === 'DELTA_CENTS') value = sign * (parseARS(v.amount) ?? 0);
  else value = parseARS(v.amount) ?? 0;

  const note = v.note.trim();
  return {
    scope: {
      all: v.scopeMode === 'all',
      categoryIds: v.scopeMode === 'categories' ? v.categoryIds : [],
      itemIds: v.scopeMode === 'items' ? v.itemIds : [],
      includeVariants: v.includeVariants,
    },
    op: { type: v.opType, value },
    rounding: v.roundingMode === 'NONE' ? { mode: 'NONE' } : { mode: v.roundingMode, stepCents: v.stepCents },
    ...(note ? { note } : {}),
  };
}
